/** Data-gestuurde vragenlijst-renderer (v4).
 *
 * Rendert secties + velden uit een `vragen_json` (de "secties"-doelvorm, zie
 * backend/app/vragenlijst_v4_seed.json) via een type-switch. Hergebruikt
 * FormFields.jsx zodat alle mobiele garanties behouden blijven:
 *   - 16px anti-zoom inputs (TextField)
 *   - min-h-[44px] tikdoelen
 *   - grid grid-cols-6 schaal (ScaleField)
 *
 * Ondersteunde veld-`type`-waarden:
 *   number, text, date, email, tel   → TextField
 *   choice                           → RadioGroupField
 *   multi_select                     → CheckboxGroupField
 *   scale_0_10                       → ScaleField (0..10)
 *   scale_1_5, agree_1_5             → ScaleField (1..5)
 *
 * Conditionele zichtbaarheid:
 *   - veld.toon_als = { veld, bevat }  → alleen tonen als het bronveld die
 *     waarde bevat (array) of gelijk is (scalair). Bv. klachten_elders_anders.
 *   - sectie.alleen_vervolg / veld.alleen_vervolg = true → alleen bij een
 *     vervolgmeting (isVervolg), niet bij de nulmeting. Bv. GRC/klacht_verandering.
 *
 * De renderer is "dom": geen step-navigatie, geen validatie-state — dat blijft
 * in VragenlijstWizard. Deze component vertaalt puur data → velden.
 */
import React from "react";
import { CheckboxGroupField, FieldRow, RadioGroupField, ScaleField, TextField } from "./FormFields";

function testidVoor(id) {
  return `field-${String(id).replace(/_/g, "-")}`;
}

/** Bepaalt of een veld met toon_als op dit moment zichtbaar moet zijn. */
function toonAlsActief(toonAls, antwoorden) {
  if (!toonAls || !toonAls.veld) return true;
  const bron = antwoorden?.[toonAls.veld];
  if (Array.isArray(bron)) return bron.includes(toonAls.bevat);
  return bron === toonAls.bevat;
}

/** Detecteert een "exclusieve" optie (bv. "geen") die de andere uitsluit. */
function exclusieveOptie(veld) {
  if (veld.exclusief) return veld.exclusief;
  const heeftGeen = (veld.opties || []).some((o) => o.value === "geen");
  return heeftGeen ? "geen" : undefined;
}

/** Rendert één veld op basis van zijn type. */
function VeldRenderer({ id, veld, waarde, onUpdate, schalen }) {
  const testid = testidVoor(id);

  switch (veld.type) {
    case "number": {
      // Als text-input met inputmode zodat iOS niet inzoomt (TextField = 16px).
      return (
        <TextField
          name={id}
          type="text"
          inputMode={veld.inputmode || "numeric"}
          value={waarde ?? ""}
          onChange={(v) => onUpdate(id, v)}
          testid={testid}
          ariaLabel={veld.vraag}
          placeholder={veld.placeholder}
        />
      );
    }
    case "text":
    case "date":
    case "email":
    case "tel": {
      const htmlType = veld.type === "text" ? "text" : veld.type;
      return (
        <TextField
          name={id}
          type={htmlType}
          value={waarde ?? ""}
          onChange={(v) => onUpdate(id, v)}
          testid={testid}
          ariaLabel={veld.vraag}
          placeholder={veld.placeholder}
        />
      );
    }
    case "choice": {
      return (
        <RadioGroupField
          name={id}
          value={waarde ?? ""}
          onChange={(v) => onUpdate(id, v)}
          options={veld.opties || []}
          testid={testid}
        />
      );
    }
    case "multi_select": {
      return (
        <CheckboxGroupField
          name={id}
          value={Array.isArray(waarde) ? waarde : []}
          onChange={(v) => onUpdate(id, v)}
          options={veld.opties || []}
          testid={testid}
          exclusive={exclusieveOptie(veld)}
        />
      );
    }
    case "scale_0_10":
    case "scale_1_5":
    case "agree_1_5": {
      const is010 = veld.type === "scale_0_10";
      const min = veld.min ?? (is010 ? 0 : 1);
      const max = veld.max ?? (is010 ? 10 : 5);
      // Eindpunt-labels: eerst expliciet op het veld, anders uit de schaal-ref.
      let labelMin = veld.label_min;
      let labelMax = veld.label_max;
      if ((!labelMin || !labelMax) && veld.schaal_ref && schalen?.[veld.schaal_ref]) {
        const s = schalen[veld.schaal_ref].labels || {};
        labelMin = labelMin || s[String(min)];
        labelMax = labelMax || s[String(max)];
      }
      const waardeNum = waarde === null || waarde === undefined || waarde === "" ? null : Number(waarde);
      return (
        <ScaleField
          name={id}
          value={waardeNum}
          onChange={(v) => onUpdate(id, v)}
          min={min}
          max={max}
          testid={testid}
          ariaLabel={veld.vraag}
          labels={{ min: labelMin, max: labelMax }}
        />
      );
    }
    default: {
      // Onbekend type: val terug op een tekstveld zodat er niets stil wegvalt.
      return (
        <TextField
          name={id}
          value={waarde ?? ""}
          onChange={(v) => onUpdate(id, v)}
          testid={testid}
          ariaLabel={veld.vraag}
        />
      );
    }
  }
}

/** Optionele schaal-uitleg-balk voor een sectie met 1-5 schaal-velden. */
function SchaalUitleg({ sectieVelden, veldenDef, schalen }) {
  const refVeld = (sectieVelden || [])
    .map((id) => veldenDef?.[id])
    .find((v) => v && v.schaal_ref && schalen?.[v.schaal_ref]);
  if (!refVeld) return null;
  const labels = schalen[refVeld.schaal_ref].labels || {};
  const nrs = Object.keys(labels).sort((a, b) => Number(a) - Number(b));
  if (nrs.length === 0) return null;
  return (
    <div className="fulkra-schaaluitleg">
      <span className="font-medium">Schaal:</span>{" "}
      {nrs.map((n, i) => (
        <span key={n}>
          {i > 0 && <span> &nbsp;·&nbsp; </span>}
          {n} = {labels[n]}
        </span>
      ))}
    </div>
  );
}

/** Rendert één sectie (kop, intro, velden). */
function SectieRenderer({ sectie, schema, antwoorden, onUpdate, foutBerichten = {} }) {
  const veldenDef = schema.velden || {};
  const schalen = schema.schalen || {};
  const testid = `sectie-${sectie.key}`;
  const zichtbareVelden = (sectie.velden || []).filter((id) => {
    const veld = veldenDef[id];
    if (!veld) return false;
    return toonAlsActief(veld.toon_als, antwoorden);
  });

  return (
    <section data-testid={testid} aria-labelledby={`${testid}-heading`}>
      {sectie.titel && (
        <h2 id={`${testid}-heading`} className="fulkra-stapkop">
          {sectie.titel}
        </h2>
      )}
      {sectie.intro ? <p className="fulkra-stapintro">{sectie.intro}</p> : null}

      <SchaalUitleg sectieVelden={sectie.velden} veldenDef={veldenDef} schalen={schalen} />

      <div className="fulkra-vragen">
        {zichtbareVelden.map((id) => {
          const veld = veldenDef[id];
          return (
            <FieldRow
              key={id}
              id={`veld-rij-${String(id).replace(/_/g, "-")}`}
              label={veld.vraag}
              required={!!veld.verplicht}
              error={foutBerichten[id]}
            >
              <VeldRenderer
                id={id}
                veld={veld}
                waarde={antwoorden?.[id]}
                onUpdate={onUpdate}
                schalen={schalen}
              />
            </FieldRow>
          );
        })}
      </div>
    </section>
  );
}

/**
 * VragenlijstRenderer — rendert de secties uit een `schema` (vragen_json).
 *
 * Props:
 *   schema       vragen_json met { secties, velden, schalen }
 *   antwoorden   huidige antwoord-state (object)
 *   onUpdate     (key, value) => void
 *   sectieKeys   optioneel: alleen deze sectie-keys renderen (voor step-modus).
 *                Zonder deze prop worden ALLE (zichtbare) secties gerenderd.
 *   isVervolg    true = vervolgmeting → toont ook `alleen_vervolg`-secties/velden.
 *   foutBerichten optioneel: { veld_id: melding } → markeert die velden rood +
 *                 toont de melding eronder (leeg/bereik-fouten uit de wizard).
 */
export function VragenlijstRenderer({ schema, antwoorden = {}, onUpdate, sectieKeys = null, isVervolg = false, foutBerichten = {} }) {
  if (!schema || !Array.isArray(schema.secties)) return null;

  const secties = schema.secties.filter((s) => {
    if (sectieKeys && !sectieKeys.includes(s.key)) return false;
    if (s.alleen_vervolg && !isVervolg) return false;
    return true;
  });

  return (
    <div data-testid="vragenlijst-renderer">
      {secties.map((sectie, idx) => (
        <div key={sectie.key} className={idx > 0 ? "mt-8" : ""}>
          <SectieRenderer sectie={sectie} schema={schema} antwoorden={antwoorden} onUpdate={onUpdate} foutBerichten={foutBerichten} />
        </div>
      ))}
    </div>
  );
}

/**
 * Hulpfunctie voor de wizard: geeft de zichtbare secties in volgorde terug
 * (respecteert alleen_vervolg). Elke zichtbare sectie = één wizard-stap.
 */
export function zichtbareSecties(schema, isVervolg = false) {
  if (!schema || !Array.isArray(schema.secties)) return [];
  return schema.secties.filter((s) => !(s.alleen_vervolg && !isVervolg));
}

/**
 * Hulpfunctie: verplichte veld-id's van een sectie die op dit moment zichtbaar
 * zijn (respecteert toon_als). Gebruikt door de wizard-validatie per stap.
 */
export function verplichteVeldenVanSectie(sectie, schema, antwoorden) {
  const veldenDef = schema?.velden || {};
  return (sectie?.velden || []).filter((id) => {
    const veld = veldenDef[id];
    if (!veld || !veld.verplicht) return false;
    return toonAlsActief(veld.toon_als, antwoorden);
  });
}

/**
 * Hulpfunctie: álle op dit moment zichtbare veld-id's van een sectie
 * (respecteert toon_als, ongeacht verplicht). Gebruikt door de wizard-
 * bereikcontrole zodat ook een optioneel-maar-ingevuld getal buiten bereik
 * vóór submit wordt gevangen.
 */
export function zichtbareVeldenVanSectie(sectie, schema, antwoorden) {
  const veldenDef = schema?.velden || {};
  return (sectie?.velden || []).filter((id) => {
    const veld = veldenDef[id];
    if (!veld) return false;
    return toonAlsActief(veld.toon_als, antwoorden);
  });
}
